import { useCallback, useEffect, useState } from 'react';
import {
  ArrowUpRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import slideSovereignSecurity from '@/assets/auth/slide-sovereign-security.png';
import slideKnowledgeSearch from '@/assets/auth/slide-knowledge-search.png';
import slideDocgenProductivity from '@/assets/auth/slide-docgen-productivity.png';
import slideSpecializedAssistants from '@/assets/auth/slide-specialized-assistants.png';

const SLIDE_INTERVAL_MS = 6000;

type MarketingSlide = {
  id: string;
  tag: string;
  title: string;
  description: string;
  coverImage: string;
  coverAlt: string;
  ctaText: string;
  ctaLink: string;
};

const MARKETING_SLIDES: readonly MarketingSlide[] = [
  {
    id: 'sovereign-security',
    tag: 'Souveraineté & Sécurité',
    title: "L'IA d'entreprise souveraine et sécurisée",
    description:
      'Toute la puissance de l’IA, hébergée en France : vos conversations et vos documents restent confidentiels et ne quittent jamais votre environnement.',
    coverImage: slideSovereignSecurity,
    coverAlt: 'Tableau de bord de sécurité et conformité Inersia',
    ctaText: 'En savoir plus',
    ctaLink: 'https://inersio.ai/',
  },
  {
    id: 'knowledge-search',
    tag: 'Recherche Documentaire',
    title: 'Vos connaissances internes, accessibles instantanément',
    description:
      'Interrogez l’ensemble de vos documents d’entreprise en langage naturel et obtenez des réponses précises avec citations de sources vérifiables.',
    coverImage: slideKnowledgeSearch,
    coverAlt: 'Recherche documentaire et analyse de documents Inersia',
    ctaText: 'En savoir plus',
    ctaLink: 'https://inersio.ai/',
  },
  {
    id: 'docgen-productivity',
    tag: 'Génération Documentaire',
    title: 'Automatisez vos rapports et documents complexes',
    description:
      'Générez des livrables professionnels structurés en quelques clics à partir de vos modèles métiers et de vos données d’entreprise.',
    coverImage: slideDocgenProductivity,
    coverAlt: 'Éditeur de génération documentaire assistée par IA Inersia',
    ctaText: 'En savoir plus',
    ctaLink: 'https://inersio.ai/',
  },
  {
    id: 'specialized-assistants',
    tag: 'Assistants Métiers',
    title: 'Un assistant qui parle le langage de votre entreprise',
    description:
      'Adaptez son ton, ses consignes et son expertise à vos métiers pour des réponses réellement utiles et personnalisées au quotidien.',
    coverImage: slideSpecializedAssistants,
    coverAlt: 'Aperçu des assistants spécialisés par métier Inersia',
    ctaText: 'En savoir plus',
    ctaLink: 'https://inersio.ai/',
  },
] as const;

interface AuthMarketingHighlightsProps {
  className?: string;
}

export function AuthMarketingHighlights({ className }: AuthMarketingHighlightsProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < MARKETING_SLIDES.length - 1;

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => Math.min(prev + 1, MARKETING_SLIDES.length - 1));
  }, []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  // Auto-advance slides every 6000ms with smooth looping
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % MARKETING_SLIDES.length);
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isPaused]);

  return (
    <div
      className={cn('flex h-full w-full min-h-0 flex-col justify-between text-left', className)}
      aria-roledescription="carousel"
      aria-label="Points forts de la plateforme Inersia"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      {/* Top Section: Visual Cover Sliding Track */}
      <div className="flex w-full min-h-0 flex-col">
        {/* Cover Screen Container (Aspect 16:11.5 with max-h-[46vh] for generous visual presence) */}
        <div className="relative aspect-[16/11.5] max-h-[46vh] w-full shrink-0 overflow-hidden rounded-xl bg-black">
          <div
            className="flex h-full w-full transition-transform duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {MARKETING_SLIDES.map((slide) => (
              <div key={slide.id} className="h-full w-full shrink-0">
                <img
                  src={slide.coverImage}
                  alt={slide.coverAlt}
                  className="size-full object-cover object-top select-none"
                  loading="eager"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Narrative Content Sliding Track */}
        <div className="relative w-full overflow-hidden">
          <div
            className="flex w-full transition-transform duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {MARKETING_SLIDES.map((slide) => (
              <div key={slide.id} className="flex w-full shrink-0 flex-col items-start pt-5 text-left sm:pt-6 lg:pt-7">
                {/* Kicker / Category Tag */}
                <span className="text-[1.2rem] font-medium text-zinc-400">
                  {slide.tag}
                </span>

                {/* Prominent Headline - Extra Bold & Large */}
                <h2 className="mt-2 text-balance text-3xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-[2.25rem] xl:text-[2.35rem]">
                  {slide.title}
                </h2>

                {/* Narrative Description */}
                <p className="mt-3 max-w-[540px] text-pretty text-sm leading-relaxed text-zinc-400 sm:text-base sm:leading-relaxed">
                  {slide.description}
                </p>

                {/* CTA Button directly under description */}
                <div className="mt-5 sm:mt-6">
                  <a
                    href={slide.ctaLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group inline-flex items-center gap-2 rounded-lg bg-[#18181b] px-5 py-2.5 sm:px-6 sm:py-3 text-base font-semibold text-white shadow-none transition-colors hover:bg-[#27272a] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                  >
                    <span>{slide.ctaText}</span>
                    <ArrowUpRightIcon className="size-4.5 text-zinc-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Right: Carousel Navigation Arrow Buttons */}
      <div className="mt-auto flex w-full items-center justify-end pt-3 shrink-0">
        <div className="flex items-center gap-2 shrink-0" role="group" aria-label="Contrôles du carrousel">
          <button
            type="button"
            onClick={handlePrev}
            disabled={!canPrev}
            aria-label="Slide précédente"
            className={cn(
              'inline-flex size-10 items-center justify-center rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400',
              canPrev
                ? 'cursor-pointer bg-[#27272a] text-zinc-200 hover:bg-[#34343a] hover:text-white active:scale-95'
                : 'pointer-events-none cursor-not-allowed bg-[#18181b] text-zinc-600 opacity-50',
            )}
          >
            <ChevronLeftIcon className="size-4.5" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canNext}
            aria-label="Slide suivante"
            className={cn(
              'inline-flex size-10 items-center justify-center rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400',
              canNext
                ? 'cursor-pointer bg-[#27272a] text-zinc-200 hover:bg-[#34343a] hover:text-white active:scale-95'
                : 'pointer-events-none cursor-not-allowed bg-[#18181b] text-zinc-600 opacity-50',
            )}
          >
            <ChevronRightIcon className="size-4.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Animated SVG Shield with emerald energy pulse */
function AnimatedSovereignShield({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      <div className="absolute inset-0 rounded-full bg-emerald-500/25 blur-sm animate-pulse" />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="relative size-full text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.7)]"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" className="fill-emerald-500/10" />
        <path d="m9 12 2 2 4-4" strokeWidth="2.2" className="animate-[pulse_2.5s_ease-in-out_infinite]" />
      </svg>
    </div>
  );
}

/** Animated SVG Lock with cyan security glow */
function AnimatedProtectedLock({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      <div className="absolute inset-0 rounded-full bg-cyan-500/25 blur-sm animate-pulse" />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="relative size-full text-cyan-400 drop-shadow-[0_0_6px_rgba(34,211,238,0.7)]"
      >
        <rect width="18" height="11" x="3" y="11" rx="2.5" ry="2.5" className="fill-cyan-500/10" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        <circle cx="12" cy="16.5" r="1.2" fill="currentColor" />
      </svg>
    </div>
  );
}

/** Animated SVG Lightning Bolt with electric energy shimmer */
function AnimatedLightningBolt({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      <div className="absolute inset-0 rounded-full bg-amber-500/30 blur-sm animate-pulse" />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="relative size-full text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]"
      >
        <path
          d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"
          className="fill-amber-400/25 animate-[pulse_2s_ease-in-out_infinite]"
        />
      </svg>
    </div>
  );
}

/** 3-Pillar Trust & Compliance Ribbon with Animated Modern SVGs */
export function AuthTrustTriptych({ className }: { className?: string }) {
  return (
    <div className={cn('grid w-full max-w-xl grid-cols-3 gap-3 border-t border-white/10 pt-5 sm:gap-4', className)}>
      <div className="flex flex-col items-center gap-1.5 text-center">
        <AnimatedSovereignShield className="size-5 sm:size-5.5" />
        <span className="text-xs font-semibold text-white sm:text-sm">100% Souverain</span>
        <span className="text-[0.75rem] text-zinc-300 sm:text-xs">Hébergé en France</span>
      </div>
      <div className="flex flex-col items-center gap-1.5 text-center">
        <AnimatedProtectedLock className="size-5 sm:size-5.5" />
        <span className="text-xs font-semibold text-white sm:text-sm">Sécurisé & Confidentiel</span>
        <span className="text-[0.75rem] text-zinc-300 sm:text-xs">Données chiffrées et protégées</span>
      </div>
      <div className="flex flex-col items-center gap-1.5 text-center">
        <AnimatedLightningBolt className="size-5 sm:size-5.5" />
        <span className="text-xs font-semibold text-white sm:text-sm">IA de pointe</span>
        <span className="text-[0.75rem] text-zinc-300 sm:text-xs">Modèles dernière génération</span>
      </div>
    </div>
  );
}

